import { useStore } from '../../../../../../contexts/store-provider';
import { useFormikContext } from 'formik';
import React, { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  PlusIcon,
  RemoveIcon,
} from '../../../../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../../../../translations/format-message';
import { IProductFiles } from '../../../../../../models/response/IProductResponse';
import ConfirmDialog from '../../../confirm-dialog';

// Matches the API shape for ProductLEDs
export interface ILEDRow {
  Id?: number;
  LEDId?: string;       // Title
  CenterValue?: number | string; // 基準値
  MaxValue?: number | string;    // 公差上限
  MinValue?: number | string;    // 公差下限
  __isNew?: boolean;
}

const EMPTY_ROW: ILEDRow = {
  Id: 0,
  LEDId: '',
  CenterValue: '',
  MaxValue: '',
  MinValue: '',
  __isNew: true,
};

const ledRowHasData = (row: ILEDRow): boolean => {
  const hasValue = (val: any) => val !== '' && val !== null && val !== undefined;
  return (
    String(row?.LEDId || '').trim() !== '' ||
    hasValue(row?.CenterValue) ||
    hasValue(row?.MaxValue) ||
    hasValue(row?.MinValue)
  );
};

interface IProps {
  id?: number;
  selectedProcess?: number;
  selectedImage?: IProductFiles[];
  selectedImageIndex?: number;
}

const LEDTableView: React.FC<IProps> = () => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { productStore } = useStore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteRowIndex, setPendingDeleteRowIndex] = useState<number | null>(null);

  // ProductLEDs lives at the top-level Formik values (not inside Process)
  const ledRows: ILEDRow[] = useMemo(() => {
    const formikData = values?.ProductLEDs || values?.ProductLeds || values?.productLEDs;
    const rawData =
      Array.isArray(formikData)
        ? formikData
        : Array.isArray(productStore?.product?.ProductLEDs) && productStore.product.ProductLEDs.length > 0
        ? productStore.product.ProductLEDs
        : Array.isArray(productStore?.product?.ProductLeds) && productStore.product.ProductLeds.length > 0
        ? productStore.product.ProductLeds
        : null;

    if (rawData) {
      return rawData.filter((row: ILEDRow) => row.__isNew || ledRowHasData(row));
    }

    return [];
  }, [
    values?.ProductLEDs,
    values?.ProductLeds,
    values?.productLEDs,
    productStore?.product?.ProductLEDs,
    productStore?.product?.ProductLeds,
  ]);

  const updateLEDData = (newRows: ILEDRow[]) => {
    setFieldValue('ProductLEDs', newRows);
  };

  // Positive decimal numbers only - strip anything else and collapse to a single
  // decimal point, keeping the raw string.
  const sanitizeDecimalInput = (raw: string): string => {
    const digitsAndDots = raw.replace(/[^0-9.]/g, '');
    const firstDotIndex = digitsAndDots.indexOf('.');
    if (firstDotIndex === -1) return digitsAndDots;
    return (
      digitsAndDots.slice(0, firstDotIndex + 1) +
      digitsAndDots.slice(firstDotIndex + 1).replace(/\./g, '')
    );
  };

  const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const navigationKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Home',
      'End',
    ];

    if (e.ctrlKey || e.metaKey) return;
    if (navigationKeys.includes(e.key)) return;

    const target = e.target as HTMLInputElement;
    if (e.key === '.' && target.value.includes('.')) {
      e.preventDefault();
      return;
    }

    if (!/^[0-9.]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleInputChange = (
    index: number,
    field: keyof ILEDRow,
    value: string
  ) => {
    const updated = [...ledRows];
    const numericFields: (keyof ILEDRow)[] = ['CenterValue', 'MaxValue', 'MinValue'];
    updated[index] = {
      ...updated[index],
      [field]: numericFields.includes(field)
        ? sanitizeDecimalInput(value)
        : value,
      __isNew: true,
    };
    updateLEDData(updated);
  };

  const handleAddRow = () => {
    updateLEDData([...ledRows, { ...EMPTY_ROW }]);
  };

  const removeRowHandler = (index: number) => {
    setPendingDeleteRowIndex(index);
    setShowDeleteConfirm(true);
  };

  const confirmRemoveRowHandler = () => {
    if (pendingDeleteRowIndex === null) return;
    updateLEDData(ledRows.filter((_, idx) => idx !== pendingDeleteRowIndex));
    setPendingDeleteRowIndex(null);
    setShowDeleteConfirm(false);
  };

  const cancelRemoveRowHandler = () => {
    setPendingDeleteRowIndex(null);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      {showDeleteConfirm && (
        <ConfirmDialog
          show={showDeleteConfirm}
          title={formatMessage('delete_row')}
          message={formatMessage('message_delete_row_confirm')}
          onCancel={cancelRemoveRowHandler}
          onConfirm={confirmRemoveRowHandler}
        />
      )}
      <div className="container1">
        <div className="importPrint product-Import">
          <Button
            type="button"
            className="btn btn-secondary d-flex align-items-center gap-2"
            onClick={handleAddRow}
          >
            <PlusIcon />
            {formatMessage('add_new_row_led')}
          </Button>
        </div>
      </div>
      <div className="tableDevices product-img tableDesign">
        <div className="table-responsive">
          <div className="react-bootstrap-table">
            <table className="table table-hover table-bordered">
              <thead>
                <tr>
                  <th className="action-delete"></th>
                  <th>{formatMessage('no')}</th>
                  <th>Title</th>
                  <th>{formatMessage('min_tolerance')} (mm)</th>
                  <th>{formatMessage('center_value')} (mm)</th>
                  <th>{formatMessage('max_tolerance')} (mm)</th>
                </tr>
              </thead>
              <tbody>
                {ledRows.map((row, index) => (
                  <tr key={row.Id || index}>
                    <td>
                      <div className="d-flex">
                        <button
                          className="btn btn-light-danger btn-sm"
                          title={formatMessage('delete')}
                          type="button"
                          onClick={() => removeRowHandler(index)}
                        >
                          <RemoveIcon />
                        </button>
                      </div>
                    </td>
                    <td>{index + 1}</td>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={row.LEDId ?? ''}
                        onChange={(e) =>
                          handleInputChange(index, 'LEDId', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        value={row.MinValue ?? ''}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) =>
                          handleInputChange(index, 'MinValue', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        value={row.CenterValue ?? ''}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) =>
                          handleInputChange(index, 'CenterValue', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        value={row.MaxValue ?? ''}
                        onKeyDown={handleNumericKeyDown}
                        onChange={(e) =>
                          handleInputChange(index, 'MaxValue', e.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default LEDTableView;
